import { normalizeNumber } from "helpers/constants";
import { useTranslation } from "@i18n";
import { useMemo, useRef, useState } from "react";
import type { ComponentProps } from "react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { ProjectWBS } from "@Features/projects";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_WARNING } from "@Services/constants";
import { linkWBSSources } from "@Features/commercial-shared/contracts/constant";
import { contractWBSServicesUrl } from "@Services/dataManager";
import { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/** Per-item custom amount/percent values, keyed by WBS originalItemId. */
type CustomValues = Record<string, { amount: number; percent: number }>;

/**
 * Shape of a row returned by the WBS grid's `dataView.getAllSelectedItems()`. This declaration is
 * where the grid's ids enter the type system: `_id` (project WBS item) and `originalItemId` (the
 * project WBS item a contract WBS row points back to) are the 24-hex ObjectIds the backend feeds to
 * `new ObjectId(...)` — see `contractWBSLinkItemsRequest.data.wbsItemIds` in contracts.schema.ts.
 */
interface SelectedWBSItem {
  _id: ObjectIdString;
  originalItemId: ObjectIdString;
  isAlreadyAddedInThisContract?: boolean;
  isAlreadyAddedInThisForm?: boolean;
  hasChild?: boolean;
  revisedAmount: number;
  boqItemsAmount: number;
}

/** Payload passed to the parent's `setSelectedWBSItemsToAttach` callback. */
type SelectedWBSItemsToAttach =
  | { itemIds: ObjectIdString[] }
  | { itemIds: ObjectIdString[]; customValues: CustomValues }
  | { itemId: ObjectIdString; boqAvailableAmount: number };

/** Config object forwarded to the underlying `ProjectWBS` grid wrapper. */
interface LinkWBSConfigParams {
  dataSourceQuery?: object;
  dataSourceUrl?: string;
  idMapping?: string;
  showColumnForParentWbs?: boolean;
  renameHeaders?: boolean;
  allowSingleRowSelection?: boolean;
  showBoqBudgetColumn?: boolean;
  showAvailableBudgetColumn?: boolean;
}

interface LinkWBSProps {
  hideLinkWbsModal: () => void;
  wbsSource: string;
  setSelectedWBSItemsToAttach: (payload: SelectedWBSItemsToAttach) => void;
  /** A Contract id is always a 24-hex ObjectId; branded so it matches `ProjectWBS.contractId`. */
  contractId?: ObjectIdString;
  formId?: string;
  allowSingleSelection?: boolean;
  btnText?: string;
  selectedWBSItems?: SelectedWBSItem[];
  /** Passed by some callers but not consumed here; declared to allow the prop. */
  parentContractId?: string;
}

function LinkWBS({
  hideLinkWbsModal,
  wbsSource,
  setSelectedWBSItemsToAttach,
  contractId,
  formId,
  allowSingleSelection = false,
  btnText = "Link",
  selectedWBSItems = [],
}: LinkWBSProps) {
  const { t } = useTranslation();
  const [linkWBSBtn, setLinkWBSBtn] = useState(false);
  const dispatch = useDispatch();
  const [customValues, setCustomValues] = useState<CustomValues>({});
  const contractDetails = useSelector((state: RootState) => state.contractDetails);

  /** The grid handle `ProjectWBS` forwards; only its `dataView` is read here. */
  const WBSRef = useRef<{ dataView: { getAllSelectedItems: () => SelectedWBSItem[] } } | null>(null);
  const params = useParams();

  const linkWBSButtonStateEnable = (state: boolean) => {
    setLinkWBSBtn(state);
  };
  const handleLinkWBSClick = async () => {
    const selectedWBSItems: SelectedWBSItem[] = WBSRef.current!.dataView.getAllSelectedItems();

    let isAnyItemAlreadyPresent = false;
    const itemIds = selectedWBSItems
      .map((data: SelectedWBSItem) => {
        if (data.isAlreadyAddedInThisContract || data.isAlreadyAddedInThisForm) {
          isAnyItemAlreadyPresent = true;
        }
        return wbsSource == linkWBSSources.projectWBS ? data._id : data.originalItemId;
      })
      .filter(Boolean);
    if (isAnyItemAlreadyPresent) {
      dispatch(
        showToastNotification({
          content: t("One of the selected Item is already linked in the contract"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else if (wbsSource == linkWBSSources.projectWBS) {
      setSelectedWBSItemsToAttach({ itemIds });
    } else if (wbsSource == linkWBSSources.parentContractWBS) {
      setSelectedWBSItemsToAttach({ itemIds, customValues });
    } else if (wbsSource == linkWBSSources.contractWBS) {
      if (selectedWBSItems[0]!.hasChild) {
        dispatch(
          showToastNotification({
            content: t("Parent WBS Items cannot be attached"),
            type: TOAST_TYPE_WARNING,
          }),
        );
      } else {
        const boqAvailableAmount = normalizeNumber(
          selectedWBSItems[0]!.revisedAmount - selectedWBSItems[0]!.boqItemsAmount,
        );
        setSelectedWBSItemsToAttach({
          itemId: selectedWBSItems[0]!.originalItemId,
          boqAvailableAmount,
        });
      }
    } else if (wbsSource == linkWBSSources.contractWBSInForm) {
      let isPresentPresent = false;
      selectedWBSItems.map((item: SelectedWBSItem) => {
        if (item.hasChild) {
          isPresentPresent = true;
        }
      });

      if (isPresentPresent) {
        dispatch(
          showToastNotification({
            content: t("Parent WBS Items cannot be attached"),
            type: TOAST_TYPE_WARNING,
          }),
        );
      } else {
        setSelectedWBSItemsToAttach({ itemIds });
      }
    }
  };
  const cancelLinking = () => {
    hideLinkWbsModal();
  };

  const linkWBSFooter = () => (
    <div className=" flex gap-4">
      <ButtonComponent disabled={!linkWBSBtn} buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleLinkWBSClick}>
        {btnText}
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={cancelLinking}>
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );
  const linkWBSHeader = () => {
    let subHeader = "Changes done in project WBS after linking will not effect already linked WBS";
    if ([linkWBSSources.contractWBS, linkWBSSources.contractWBSInForm].includes(wbsSource)) {
      subHeader = "All the available WBS items in this contract";
    } else if (wbsSource == linkWBSSources.parentContractWBS) {
      subHeader = "All the available WBS items in parent contract";
    }
    return (
      <div className=" w-full flex justify-between rounded-md">
        <div className="flex flex-col gap-1">
          <div>{t("WBS Items List")}</div>
          <span className="text-sm font-normal">{subHeader}</span>
        </div>
      </div>
    );
  };

  const projectWBSQuery = useMemo(
    () => ({
      contractId,
      viewType: "WBSView",
      projectId: params.projectId,
      contractVersionId: contractDetails.versionId,
      where: [
        {
          isComplex: false,
          field: "projectId",
          operator: "equal",
          ignoreCase: false,
          value: params.projectId,
        },
      ],
    }),
    [contractDetails.versionId, contractId, params.projectId],
  );

  const parentContractWBSQuery = useMemo(
    () => ({
      contractId,
      viewType: "WBSView",
      getParentItems: true,
      contractVersionId: contractDetails.versionId,
    }),
    [contractDetails.versionId, contractId],
  );

  const contractWBSQuery = useMemo(
    () => ({
      contractId,
      viewType: "WBSView",
      contractVersionId: contractDetails.versionId,
    }),
    [contractDetails.versionId, contractId],
  );
  const contractWBSInFormQuery = useMemo(
    () => ({
      contractId,
      viewType: "WBSView",
      contractVersionId: contractDetails.versionId,
      formId,
    }),
    [contractDetails.versionId, contractId, formId],
  );
  const linkWBSContent = () => {
    let configParams: LinkWBSConfigParams = {};
    if (wbsSource == linkWBSSources.projectWBS) {
      configParams = {
        dataSourceQuery: projectWBSQuery,
      };
    } else if (wbsSource == linkWBSSources.parentContractWBS) {
      configParams = {
        dataSourceQuery: parentContractWBSQuery,
        dataSourceUrl: contractWBSServicesUrl,
        idMapping: "originalItemId",
        showColumnForParentWbs: true,
        renameHeaders: true,
      };
    } else if (wbsSource == linkWBSSources.contractWBS) {
      configParams = {
        dataSourceQuery: contractWBSQuery,
        dataSourceUrl: contractWBSServicesUrl,
        idMapping: "originalItemId",
        showColumnForParentWbs: false,
        renameHeaders: true,
        allowSingleRowSelection: allowSingleSelection,
        showBoqBudgetColumn: true,
        showAvailableBudgetColumn: true,
      };
    } else if (wbsSource == linkWBSSources.contractWBSInForm) {
      configParams = {
        dataSourceQuery: contractWBSInFormQuery,
        dataSourceUrl: contractWBSServicesUrl,
        idMapping: "originalItemId",
        showColumnForParentWbs: false,
        renameHeaders: true,
        allowSingleRowSelection: allowSingleSelection,
        showBoqBudgetColumn: true,
        showAvailableBudgetColumn: false,
      };
    }
    return (
      <ProjectWBS
        // KNOWN BUG, preserved verbatim: `ProjectWBS.selectedWBSItems` is a list of WBS item IDs
        // (it feeds `disabledRows`, compared against `data.originalItemId`), but this passes whole
        // `SelectedWBSItem` objects, so no row is ever matched. Erased intersection assertion;
        // mapping to `.originalItemId` here would change behaviour.
        selectedWBSItems={selectedWBSItems as typeof selectedWBSItems & ObjectIdString[]}
        formId={formId}
        // @ts-expect-error -- `LinkWBSConfigParams` does not match the modal's declared config shape; the two disagree on which fields are optional.
        configParams={configParams}
        // @ts-expect-error -- a `Dispatch<SetStateAction<CustomValues>>` is passed where the child declares a plain setter over a different value type — the same `SetStateAction` invariance wall as `FileManagerData`: the value arm needs one assignability direction and the updater arm the other, so no variance trick bridges two different `T`s.
        setCustomValues={setCustomValues}
        wbsSource={wbsSource}
        customValues={customValues}
        contractId={contractId}
        linkWBSButtonStateEnable={linkWBSButtonStateEnable}
        showOnlyGrid
        ref={WBSRef as typeof WBSRef & ComponentProps<typeof ProjectWBS>["ref"]}
      />
    );
  };
  return (
    <>
      <ModalStateDialogComponent
        allowModalPadding={false}
        footerContent={linkWBSFooter()}
        width="80%"
        height={"80%"}
        headerTitle={linkWBSHeader()}
        onCancel={cancelLinking}
        isVisible
        modelContent={linkWBSContent()}
      />
    </>
  );
}
export default LinkWBS;
